import { Component, inject, Inject, Optional, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { CommonModule } from '@angular/common';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, FormsModule } from '@angular/forms';
import { emailFormatValidator, phoneValidator } from '@core/shared/form-validators';
import { TranslateModule } from '@ngx-translate/core';
import { Prefix} from '@interfaces/prefix';
import {SettingsApiService} from '@services/api/settings/settings-api.service';
import { CompaniesApiService} from '@services/api/companies/companies-api.service';
import { Subject, takeUntil, finalize} from 'rxjs';
import { Company} from '@interfaces/company';
import { Roles, RoleNames} from '@enums/Roles';
import { TechnicianApiService } from '@services/api/user/technician/technician-api.service';
import { Technician } from '@interfaces/technician';
import {AuthService} from '@services/api/auth/auth.service';
import {User} from '@interfaces/user';
import {ModalsConfirmComponent} from '@components/modals/modals-confirm/modals-confirm.component';

@Component({
  selector: 'app-profile-technician',
  imports: [MaterialModule, CommonModule, ReactiveFormsModule, TablerIconsModule, TranslateModule, FormsModule],
  templateUrl: './profile.component.html',
  standalone: true,
  styleUrl: './profile.component.scss'
})
export class ProfileComponent implements OnInit, OnDestroy {
  technicianForm: FormGroup;
  public dialog = inject(MatDialog);
  protected companies: Company[] = [];
  protected companyFilter: string = '';
  private prefix_api_service = inject(SettingsApiService);
  private company_api_service = inject(CompaniesApiService);
  private technician_api_service = inject(TechnicianApiService);
  protected prefixes: Prefix[] = [];
  protected prefixFilter: string = '';
  private destroy$ = new Subject<void>();
  private user_api_service = inject(AuthService);
  protected user: User | null = null;
  protected readonly: boolean = false;

  @ViewChild('filterInput') filterInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('companyFilterInput') companyFilterInputRef!: ElementRef<HTMLInputElement>;
  readonly technicianRoleName = RoleNames[Roles.TECHNICIAN];
  readonly technicianRoleId = Roles.TECHNICIAN;

  constructor(
    public dialogRef: MatDialogRef<ProfileComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: Technician | null,
    private fb: FormBuilder)
  {
    this.technicianForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, emailFormatValidator]],
      name: ['', [Validators.required]],
      lastname: ['', [Validators.required]],
      roles: [this.technicianRoleName],
      company: ['', [Validators.required]],
      prefix: [''],
      phone: ['', phoneValidator()],
    });
  }

  ngOnInit(): void {
    this.getUserData();
    this.loadPrefixes();
    this.loadCompanies();
    this.populateFormOnEdit();
  }

  getUserData(): void {
    this.user_api_service.get_user_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.user = response.data;
          if (response.data.roles.id > Roles.MANAGEMENT_SYSTEM) {
            this.readonly = true;
          }
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  populateFormOnEdit(): void {
    if (this.data) {
      this.technicianForm.patchValue({
        name: this.data.name,
        lastname: this.data.lastname,
        email: this.data.email,
        roles: Roles[this.data.roles?.id],
        phone: this.data.phone,
        prefix: this.data.prefix?.id,
        company: this.data.company?.id
      });
    }
  }

  loadCompanies(): void {
    this.company_api_service.get_company_list()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.companies = response.data;
        },
        error: (error) => {
          console.log(error);
        }
      })
  }

  loadPrefixes(): void {
    this.prefix_api_service.get_prefixes().subscribe(
      (response) => {
        this.prefixes = response.data;
        if (!this.technicianForm.get('prefix')?.value) {
          this.setDefaultPrefix();
        }
      },
      (error) => {
        console.error('Error loading prefixes:', error);
      }
    );
  }

  filteredPrefixes(): Prefix[] {
    if (!this.prefixFilter) return this.prefixes;
    const lower = this.prefixFilter.toLowerCase();
    return this.prefixes.filter(p => p.prefix.toLowerCase().includes(lower));
  }

  filteredCompanies(): Company[] {
    if (!this.companyFilter) return this.companies;
    const lower = this.companyFilter.toLowerCase();
    return this.companies.filter(c => c.name.toLowerCase().includes(lower));
  }

  onPrefixDropdownOpened(opened: boolean): void {
    if (!opened) {
      this.prefixFilter = '';
    } else {
      setTimeout(() => {
        this.filterInputRef?.nativeElement?.focus();
      });
    }
  }

  onCompanyDropdownOpened(opened: boolean): void {
    if (!opened) {
      this.companyFilter = '';
    } else {
      setTimeout(() => {
        this.companyFilterInputRef?.nativeElement?.focus();
      });
    }
  }

  setDefaultPrefix(): void {
    const defaultPrefix = this.prefixes.find(prefix => prefix.default === true);
    if (defaultPrefix) {
      this.technicianForm.controls['prefix'].setValue(defaultPrefix.id);
    }
  }

  onPhoneInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const value = inputElement.value;
    const numericValue = value.replace(/[^0-9+\- ]/g, '');
    if (value !== numericValue) {
      inputElement.value = numericValue;
      this.technicianForm.controls['phone'].setValue(numericValue);
    }
  }

  onSubmit(): void {
    this.technicianForm.controls['roles'].setValue(this.technicianRoleId);
    if (this.technicianForm.valid) {
      if (this.data) {
        this.modifyTechnician();
      } else {
        this.addTechnician();
      }
    }
  }

  addTechnician(): void {
    this.technician_api_service.add_technician(
      this.technicianForm.get('name')?.value,
      this.technicianForm.get('lastname')?.value,
      this.technicianForm.get('email')?.value,
      this.technicianForm.get('company')?.value,
      this.technicianForm.get('phone')?.value,
      this.technicianForm.get('prefix')?.value
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dialogRef.close(response);
        },
        error: (error) => {
          this.dialogRef.close(error);
        }
      });
  }

  modifyTechnician(): void {
    if (this.data?.id){
      this.technician_api_service.update_technician(
        this.data.id,
        this.technicianForm.get('name')?.value,
        this.technicianForm.get('lastname')?.value,
        this.technicianForm.get('email')?.value,
        this.technicianForm.get('company')?.value,
        this.technicianForm.get('phone')?.value,
        this.technicianForm.get('prefix')?.value
      )
        .subscribe({
          next: (response) => {
            if (response.success) {
              this.dialogRef.close(response);
            }
          },
          error: (error) => {
            this.dialogRef.close(error);
          }
        });
    }
  }


  get canDelete(): boolean {
    return !!this.data?.id && !!this.user?.roles && this.user.roles.id < Roles.OPERATOR_BO;
  }

  deleteTechnician(): void {
    if (!this.canDelete || !this.data?.id) {
      return;
    }

    const confirmRef = this.dialog.open(ModalsConfirmComponent, {
      data: {
        title: 'technicians.delete',
        message: 'technicians.delete_confirm',
        confirmText: 'technicians.confirm',
        cancelText: 'technicians.cancel'
      },
      width: '400px',
      autoFocus: false,
    });

    confirmRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(confirmed => {
        if (!confirmed || !this.data?.id) {
          return;
        }
        this.technician_api_service.delete_technician(this.data.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.dialogRef.close({ deleted: true });
            },
            error: (error) => {
              this.dialogRef.close({ deleted: false, error });
            }
          });
      });
  }

  // closeDialog(): void {
  //   this.dialogRef.close({ event: 'Cancel' });
  // }



  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly Roles = Roles;
}
